import {Search2Icon} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Checkbox,
  Grid,
  HStack,
  Input,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr, useToast
} from "@chakra-ui/react";
import {useState} from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {useParams} from "react-router-dom";

import { downloadResultCertificatesExcel, getCandidates } from "../../shared/Api";
import { errorToast, successToast } from "../../shared/helper/ToastHelper";
import {useAuthentication} from "../../shared/hooks/AuthenticationProvider";
import {disableBlurProtection} from "../../shared/hooks/BlurProtection";
import {Authority} from "../../shared/model/Constants";
import {adminKeys} from "../../shared/QueryKeys";
import { enableKeyCommands, extractData, saveByteArray, toIsoString } from "../../shared/Utils";
import CandidateSummaryItem from "./CandidateSummaryItem";
import DownloadAnswers from "./DownloadAnswers";
import DownloadAnswersMatrix from "./DownloadAnswersMatrix";
import DownloadFilesFromList from "./DownloadFilesFromList";
import DownloadResults from "./DownloadResults";
import UploadAnswers from "./UploadAnswers";
import UploadExcelFeedback from "./UploadExcelFeedback";

const CandidatesList = () => {
  const authentication = useAuthentication();
  const queryClient = useQueryClient();
  const toast = useToast();

  enableKeyCommands();
  disableBlurProtection();

  const isAdmin =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_ADMIN);
  const isTeacher =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_TEACHER);
  const isJudge =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_JUDGE) && !isAdmin;
  const isPublic = (!isAdmin && !isTeacher && !isJudge);

  const params = useParams<{
    group: string;
    institution: string;
  }>();

  // from scandidates/*group*?toggle=c
  // for non registered users that want to enable/disable candidates
  const urlParams = new URLSearchParams(window.location.search);
  const edit = urlParams.get('toggle');
  const activateCandidates = edit === 'c' || ( !isPublic  && !isJudge );

  // from .../*group*?mock=only / all
  const mockParam = urlParams.get('mock');
  const mockCandidates = mockParam === 'all' || mockParam === 'only' ? mockParam : 'exclude';

  // comes from scandidates, can´t select the group
  const initialGroup = params.group || "";
  const inGroupMode = initialGroup !== "";

  // comes from candidatesInst
  const institutionOnly = params.institution !== undefined && params.institution !== '';

  const today: string = toIsoString(new Date()).slice(0, 10);

  const minDate: string = "2022-12-01";
  const startDate: string = today;

  const [idValue, setIdValue] = useState<string>("");
  const [nameValue, setNameValue] = useState<string>("");
  const [testValue, setTestValue] = useState<string>("");
  const [institutionValue, setInstitutionValue] = useState<string>(params.institution || "");
  const [regionValue, setRegionValue] = useState<string>("");
  const [revisionCodeValue, setRevisionCodeValue] = useState<string>("");
  const [groupValue, setGroupValue] = useState<string>(initialGroup.toLowerCase());
  const [startDateValue, setStartDateValue] = useState<string>(startDate);
  const [endDateValue, setEndDateValue] = useState<string>(today);
  const [includeInvited, setIncludeInvited] = useState<boolean>(inGroupMode);
  const [includeInProgress, setIncludeInProgress] = useState<boolean>(true);
  const [includeCompleted, setIncludeCompleted] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);

  const candidatesQuery = useQuery(adminKeys.candidatesSummary(), () =>
      getCandidates({
        startDateValue: startDateValue,
        endDateValue: endDateValue,
        includeCompleted: includeCompleted,
        includeInProgress: includeInProgress,
        includeInvited: includeInvited,
        region: regionValue.trim().toLowerCase(),
        institution: institutionValue.trim().toLowerCase(),
        isTeacher: isTeacher === true,
        isJudge: isJudge === true,
        group: groupValue.trim().toLowerCase(),
        institutionOnly: institutionOnly,
        username: idValue.trim().toLowerCase(),
        name: nameValue.trim().toLowerCase(),
        testName: testValue.trim().toLowerCase(),
        mockCandidates: mockCandidates,
        revisionCode: revisionCodeValue
      }, isPublic).then(extractData),
    {retry: true, refetchOnWindowFocus: false, enabled: inGroupMode}
  );
  function handleKeyPress (key: string) {
    if (key === 'Enter') {
      return candidatesQuery.refetch();
    }
  }

  function calculateCols() {
    const base = 4; /*<Text fontWeight="bold">Prueba</Text>
                                        <Text fontWeight="bold">Estado</Text>
                                        <Text fontWeight="bold">Inicio-Fin</Text>
                                        { !isPublic && <Text fontWeight="bold">Banda</Text> }
                                        <Text fontWeight="bold">Última pregunta</Text>
                                        { !isAdmin && activateCandidates && <Text fontWeight="bold">Habilitar</Text>}
                                        { isAdmin && <Text fontWeight="bold">Detalles</Text>}
                                        { isAdmin && <Text fontWeight="bold">Editar</Text>}
                                        */
    const notPublic = !isPublic ? 1 : 0;
    const adminCols = isAdmin ? 2 : 0;
    const judgeCols = isJudge ? 1 : 0;
    const individualActivate = !isAdmin && activateCandidates ? 1 : 0;
    return base + notPublic + adminCols + individualActivate + judgeCols;
  }

  const downloadResultsCertificatesMutation = useMutation(
    downloadResultCertificatesExcel,
    {
      onSuccess: (TData) => {
        successToast(toast, `Descargando`);
        saveByteArray("Certificados", TData.data);
        return queryClient.invalidateQueries(adminKeys.candidates());
      },
      onError: () => {
        errorToast(toast, "Error descargando");
      },
    });

    return (
      <Box width="95%" marginX="auto">
        <HStack alignSelf="start" spacing={5}>
          { isAdmin && <UploadAnswers /> }
          { isAdmin && <UploadExcelFeedback /> }

          { isAdmin && <DownloadAnswersMatrix endDateValue={endDateValue} startDateValue={startDateValue}
                                                 institution={institutionValue.trim().toLowerCase()}
                                                 region={regionValue.trim().toLowerCase()} includeInvited={includeInvited}
                                                 includeInProgress={includeInProgress} includeCompleted={includeCompleted}
                                                 isTeacher={false} name={nameValue.trim().toLowerCase()}
                                                 testName={testValue.trim()}
                                                 username={idValue.trim().toLowerCase()}
                                                 group={groupValue.trim().toLowerCase()} /> }

          { isAdmin && <DownloadAnswers endDateValue={endDateValue} startDateValue={startDateValue}
                                                 institution={institutionValue.trim().toLowerCase()}
                                                 region={regionValue.trim().toLowerCase()} includeInvited={includeInvited}
                                                 includeInProgress={includeInProgress} includeCompleted={includeCompleted}
                                                 isTeacher={false} name={nameValue.trim().toLowerCase()}
                                                 testName={testValue.trim().toLowerCase()}
                                                 username={idValue.trim().toLowerCase()}
                                                 group={groupValue.trim().toLowerCase()} /> }
          { (!isPublic) && <DownloadResults endDateValue={endDateValue} startDateValue={startDateValue}
                                                 institution={institutionValue.trim().toLowerCase()}
                                                 region={regionValue.trim().toLowerCase()} includeInvited={includeInvited}
                                                 includeInProgress={includeInProgress} includeCompleted={includeCompleted}
                                                 isTeacher={isTeacher===true} isJudge={isJudge===true}
                                                 name={nameValue.trim().toLowerCase()}
                                                 testName={testValue.trim().toLowerCase()}
                                                 username={idValue.trim().toLowerCase()}
                                                 group={groupValue.trim().toLowerCase()}
                                                 revisionCode={revisionCodeValue}
                                                 /> }
          { /* isAdmin && <DownloadFilesFromList
            onFileSent={(file) => downloadResultsCertificatesMutation.mutate(file)}
            buttonName="Descargar Certificados" modalName="Certificados" modalButton="Descargar"  /> */}

          { (isAdmin || isTeacher) && <DownloadFilesFromList
            buttonName="Descargar Constancias" modalName="Descargar constancias" modalButton="Descargar"
            isPublic
              candidateFilters={{endDateValue:endDateValue, startDateValue:startDateValue,
                                                                                 institution:institutionValue.trim().toLowerCase(),
                                                                                 region:regionValue.trim().toLowerCase(), includeInvited:includeInvited,
                                                                                 includeInProgress:includeInProgress, includeCompleted:includeCompleted,
                                                                                 isTeacher:isTeacher===true, name:nameValue.trim().toLowerCase(),
                                                                                 testName:testValue.trim().toLowerCase(),
                                                                                 username:idValue.trim().toLowerCase(),
                                                                                 group:groupValue.trim().toLowerCase()}
                                                                                 }
              /> }
        </HStack >
        <div>
          <HStack alignSelf="start" spacing={10} paddingY={1}>
            <Input type="date" id="startDate" value={startDateValue}
                   min={minDate}
                   onChange={ (event) => {
                     setStartDateValue(event.target.value);
                   }}/>
            <Input type="date" id="endDate" value={endDateValue}
                   min={minDate}
                   onChange={ (event) => {
                     setEndDateValue(event.target.value);
                   }}/>
            <Checkbox minW="fit-content"
                      onChange={ (e) => {
                        setIncludeInvited(e.target.checked);
                      }}
                      defaultChecked={includeInvited}>
              Sin iniciar
            </Checkbox>
            <Checkbox minW="fit-content"
                      onChange={ (e) => {
                        setIncludeInProgress(e.target.checked);
                      }}
                      defaultChecked={includeInProgress}>
              En proceso
            </Checkbox>
            <Checkbox minW="fit-content"
                      onChange={ (e) => {
                        setIncludeCompleted(e.target.checked);
                      }}
                      defaultChecked={includeCompleted}>
              Completados
            </Checkbox>
          </HStack >
          <HStack alignSelf="start" spacing={10} paddingY={1}>
            <Input placeholder='Identificación'
                   onKeyDown={(event) => handleKeyPress(event.key)}
                   onChange={(event)=>setIdValue(event.target.value)}/>
            <Input placeholder='Nombre'
                   onKeyDown={(event) => handleKeyPress(event.key)}
                   onChange={(event)=>setNameValue(event.target.value)} />
            { (isAdmin || isJudge) &&
              <Input placeholder='Código de revisión'
                     onKeyDown={(event) => handleKeyPress(event.key)}
                     onChange={(event)=> setRevisionCodeValue(event.target.value)}/>
            }
          </HStack >
          <HStack alignSelf="start" spacing={10} paddingY={1}>
            { (isAdmin || isJudge) &&
              <Input placeholder='Institución'
                     onKeyDown={(event) => handleKeyPress(event.key)}
                     onChange={(event)=> setInstitutionValue(event.target.value)}/>
            }
            <Input placeholder='Región'
                   onKeyDown={(event) => handleKeyPress(event.key)}
                   onChange={(event)=> setRegionValue(event.target.value)}/>
            { !inGroupMode &&
              <Input placeholder='Grupo'
                     onKeyDown={(event) => handleKeyPress(event.key)}
                     onChange={(event)=> setGroupValue(event.target.value)}/>
            }
          </HStack >
          <HStack alignSelf="start" spacing={10} paddingY={1}>
           { isAdmin &&
               <Input placeholder='Prueba'
                  onKeyDown={(event) => handleKeyPress(event.key)}
                  onChange={(event)=>setTestValue(event.target.value)} />
                       }
          </HStack >
          <HStack alignSelf="start" spacing={10} paddingY={1}>
            <Select value={pageSize.toString()} isReadOnly={true} width="1xs"
                    onChange={(event)=> {setPageSize(parseInt(event.target.value)); }}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              {/*<option value={(candidatesQuery.data).length}>Todos</option> */}
            </Select>

            <Button leftIcon={<Search2Icon />} colorScheme="teal" onClick={()=> {
              setPage(0);
              return candidatesQuery.refetch();
            }}
                    disabled={candidatesQuery.isFetching}>
              Buscar
            </Button>

            {candidatesQuery.isFetching &&
              <Spinner />
            }
          </HStack >
        </div>

        <TableContainer width="95vw">
          <Table size='sm' variant='striped' whiteSpace="normal">
            <Thead>
              <Tr>
                { ! isJudge && <Th>Identificación</Th>}
                { ! isJudge && <Th>Nombre</Th> }
                { (isAdmin || isJudge) && <Th>Código de revisión</Th> }
                { ! isJudge && <Th>Grupo</Th> }
                { ! isJudge && <Th>Institución (Región)</Th>}

                { ((!isPublic && !isJudge) || activateCandidates) && <Th> </Th> /* download certificate + activate/deactivate all */}

                <Th>
                  <Box key={"Box header"}
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.100"
                  >
                    <Grid templateColumns={`repeat(${calculateCols()}, 1fr)`} placeItems="center" columnGap={2}>
                      <Text fontWeight="bold">Prueba</Text>
                      <Text fontWeight="bold">Estado</Text>
                      <Text fontWeight="bold">Inicio-Fin</Text>
                      { !isPublic && <Text fontWeight="bold">Banda</Text> }
                      <Text fontWeight="bold">Última pregunta</Text>
                      { !isAdmin && activateCandidates && <Text fontWeight="bold">Habilitar</Text>}
                      { (isAdmin || isJudge) && <Text fontWeight="bold">Detalles</Text>}
                      { isAdmin && <Text fontWeight="bold">Editar</Text>}
                    </Grid>
                  </Box>
                </Th>
              </Tr>
            </Thead>

            <Tbody key="List">
              {candidatesQuery.isSuccess && candidatesQuery.data.length ? (
                (candidatesQuery.data).slice(page, page+pageSize).map((candidate) => (
                  <CandidateSummaryItem candidateSummary={candidate} />
                ))
              ) : (
                <Tr key={"empty"}>
                </Tr>
              )}

            </Tbody>
          </Table>
        </TableContainer>

        <HStack paddingY={10} justifyContent="end">
          <Button
            colorScheme="teal"
            disabled={page === 0}
            onClick={()=>setPage(page-pageSize)}
          >
            Anterior
          </Button>
          <Button
            colorScheme="teal"
            disabled={!candidatesQuery.data || page+pageSize >= (candidatesQuery.data).length}
            onClick={()=>setPage(page+pageSize)}
          >
            Siguiente
          </Button>
        </HStack>
      </Box>
    );

};

export default CandidatesList;
